import type { SupabaseClient } from '../../lib/supabase';
import type {
  HandoffLink,
  SubmissionPhoto,
  SubmissionStatus,
  SupplierHandoff,
  SupplierSubmission,
} from './types';

/**
 * Database access for the supplier exchange. Who sees and changes what is
 * decided entirely by Row Level Security and the guard triggers; nothing
 * here is trusted.
 */

function fail(message: string): never {
  throw new Error(message);
}

// One literal each, deliberately unwrapped (PostgREST type inference).
// prettier-ignore
const SUBMISSION_COLUMNS = 'id, supplier_organization_id, created_by, title, notes, status, cg_order_id, decided_by, decided_at, decision_note, project_id, created_at, updated_at';
const PHOTO_COLUMNS = 'id, submission_id, url, name, created_by, created_at';
// prettier-ignore
const HANDOFF_COLUMNS = 'id, supplier_organization_id, created_by, title, notes, tags, client, product_type, description, units, acknowledged_by, acknowledged_at, ack_note, handoff_po_id, is_order_link, created_at';
const LINK_COLUMNS = 'id, handoff_id, url, name, created_at';

export async function fetchSubmissions(client: SupabaseClient): Promise<SupplierSubmission[]> {
  const { data, error } = await client
    .from('supplier_submissions')
    .select(SUBMISSION_COLUMNS)
    .order('created_at', { ascending: false });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function fetchSubmissionPhotos(
  client: SupabaseClient,
  submissionId: string,
): Promise<SubmissionPhoto[]> {
  const { data, error } = await client
    .from('submission_photos')
    .select(PHOTO_COLUMNS)
    .eq('submission_id', submissionId)
    .order('created_at', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export interface SubmissionInput {
  supplierOrganizationId: string;
  title: string;
  notes: string;
  createdBy: string;
}

export async function createSubmission(
  client: SupabaseClient,
  input: SubmissionInput,
): Promise<SupplierSubmission> {
  const { data, error } = await client
    .from('supplier_submissions')
    .insert({
      supplier_organization_id: input.supplierOrganizationId,
      title: input.title.trim(),
      notes: input.notes,
      created_by: input.createdBy,
    })
    .select(SUBMISSION_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }
  return data;
}

export async function updateSubmissionNotes(
  client: SupabaseClient,
  submissionId: string,
  changes: { title?: string; notes?: string },
): Promise<void> {
  const { data, error } = await client
    .from('supplier_submissions')
    .update(changes)
    .eq('id', submissionId)
    .select('id');
  if (error) {
    fail(error.message);
  }
  if ((data ?? []).length === 0) {
    fail('submission_is_closed');
  }
}

/** TAP admin decision; approval auto-creates the tracking project. */
export async function decideSubmission(
  client: SupabaseClient,
  submissionId: string,
  status: SubmissionStatus,
  note: string,
): Promise<SupplierSubmission> {
  const { data, error } = await client.rpc('decide_supplier_submission', {
    p_submission: submissionId,
    p_status: status,
    p_note: note,
  });
  if (error) {
    fail(error.message);
  }
  return data;
}

export async function fetchHandoffs(client: SupabaseClient): Promise<SupplierHandoff[]> {
  const { data, error } = await client
    .from('supplier_handoffs')
    .select(HANDOFF_COLUMNS)
    // Link-only rows excluded (20261030000100). They exist to point a CG order
    // at a factory and carry no design brief — rendering them here asked a
    // supplier to acknowledge a handoff that does not exist, beside the real
    // acknowledgement on the order itself. Two acknowledgements for one job,
    // one of them meaningless.
    .eq('is_order_link', false)
    .order('created_at', { ascending: false });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function fetchHandoffLinks(
  client: SupabaseClient,
  handoffId: string,
): Promise<HandoffLink[]> {
  const { data, error } = await client
    .from('handoff_links')
    .select(LINK_COLUMNS)
    .eq('handoff_id', handoffId)
    .order('created_at', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export interface HandoffInput {
  supplierOrganizationId: string;
  title: string;
  notes: string;
  tags: string;
  /**
   * The four that exist to prefill a CG order later. TAP-owned — the guard
   * trigger refuses a supplier write to any of them.
   */
  client: string;
  productType: string;
  description: string;
  units: number | null;
  links: { url: string; name: string }[];
  createdBy: string;
}

export async function createHandoff(
  client: SupabaseClient,
  input: HandoffInput,
): Promise<SupplierHandoff> {
  const { data, error } = await client
    .from('supplier_handoffs')
    .insert({
      supplier_organization_id: input.supplierOrganizationId,
      title: input.title.trim(),
      client: input.client.trim(),
      product_type: input.productType.trim(),
      description: input.description.trim(),
      units: input.units,
      notes: input.notes,
      tags: input.tags.trim(),
      created_by: input.createdBy,
    })
    .select(HANDOFF_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }
  for (const link of input.links) {
    const inserted = await client.from('handoff_links').insert({
      handoff_id: data.id,
      url: link.url.trim(),
      name: link.name.trim(),
    });
    if (inserted.error) {
      fail(inserted.error.message);
    }
  }
  return data;
}

/**
 * Stamps the CG order whose Production PO was generated from this handoff.
 *
 * TAP-only by the guard trigger (supplier_handoffs_guard), which was extended
 * with this column in 20260920000100 — without that, the supplier branch of
 * the guard names the columns it protects and a new one would have sailed
 * through, letting a factory point its own handoff at somebody else's order.
 */
export async function setHandoffPo(
  client: SupabaseClient,
  handoffId: string,
  orderId: string,
): Promise<void> {
  const { error } = await client
    .from('supplier_handoffs')
    .update({ handoff_po_id: orderId })
    .eq('id', handoffId);
  if (error) {
    fail(error.message);
  }
}

export async function acknowledgeHandoff(
  client: SupabaseClient,
  handoffId: string,
  userId: string,
  note: string,
): Promise<void> {
  const { data, error } = await client
    .from('supplier_handoffs')
    .update({
      acknowledged_by: userId,
      acknowledged_at: new Date().toISOString(),
      ack_note: note,
    })
    .eq('id', handoffId)
    .select('id');
  if (error) {
    fail(error.message);
  }
  if ((data ?? []).length === 0) {
    fail('handoff_already_acknowledged');
  }
}
